import { MARKETING_RESOURCE_CHANNELS } from "@/services/marketing";
import { ResourceChannel } from "@/services/resources";
import { SOCIAL_MEDIA_RESOURCE_CHANNELS } from "@/services/social_media";
import { RouteRecordRaw } from "vue-router";
import { migrateLegacyPagesShare } from "@/store/data_filters";

export const marketingRoutes: RouteRecordRaw[] = [
  {
    path: "goals",
    alias: "/goals",
    name: "goals",
    redirect: { name: "goals-overview" },
    meta: {
      activityArea: "goals",
      title: "Goals & KPIs",
      text: "Goals & KPIs",
      description:
        "Keep track of progress towards your business goals and key performance indicators",
      added: "2021-03-15",
      icon: "bullseye-arrow-light",
      requiredResources: [ResourceChannel.GOOGLE_ANALYTICS_4],
      breadcrumb: { label: "Goals & KPIs", to: { name: "goals-overview" } },
    },
    component: () => import("@/views/Goals/GoalsBase.vue"),
    children: [
      {
        path: "overview",
        name: "goals-overview",
        meta: { title: "Goals & KPIs overview", breadcrumb: false },
        component: () => import("@/views/Goals/Goals.vue"),
      },
      {
        path: ":pk",
        name: "goal-details",
        meta: {
          title: "Goal Details",
          breadcrumb: {
            label: "Goal",
            dynamic: true,
            fallback: "Goal",
          },
        },
        component: () => import("@/views/Goals/GoalDetails.vue"),
      },
      {
        path: "kpis/:pk",
        name: "kpi_details",
        meta: {
          title: "KPI Details",
          breadcrumb: { label: "KPI", dynamic: true, fallback: "KPI" },
        },
        component: () => import("@/views/KPIs/KPIDetails.vue"),
      },
    ],
  },
  {
    path: "ad-performance",
    alias: "/ad-performance",
    name: "ad-performance-base",
    redirect: { name: "ad-performance-overview" },
    meta: {
      activityArea: "ad_performance",
      title: "Ad Performance",
      text: "Ad Performance",
      description: "Stay on top of all your digital marketing campaigns",
      icon: "rectangle-ad-light",
      updated: "2024-05-24",
      requiredResources: MARKETING_RESOURCE_CHANNELS,
      helpDeskPath: "marketing-performance",
      breadcrumb: {
        label: "Ad Performance",
        to: { name: "ad-performance-overview" },
      },
    },
    component: () =>
      import("@/views/MarketingPerformance/MarketingPerformanceBase.vue"),
    children: [
      {
        path: "overview",
        name: "ad-performance-overview",
        meta: { title: "Overview", breadcrumb: false },
        component: () =>
          import("@/views/MarketingPerformance/MarketingPerformanceOverviewTab.vue"),
      },
      {
        path: "ads",
        name: "marketing-performance-ads",
        meta: { title: "Ads", breadcrumb: false },
        component: () =>
          import("@/views/MarketingPerformance/MarketingPerformanceAdsTab.vue"),
      },
    ],
  },
  {
    path: "social-media",
    alias: "/social-media",
    name: "social-media",
    redirect: { name: "social-media-account-summary" },
    meta: {
      activityArea: "social",
      title: "Social Media",
      text: "Social Media",
      gtmEventCategory: "Reporting - Social media",
      icon: "thumbs-up-light",
      description: "Get on top of your social media efforts",
      added: "2021-06-10",
      requiredResources: SOCIAL_MEDIA_RESOURCE_CHANNELS,
      helpDeskPath: "social-media",
      breadcrumb: {
        label: "Social Media",
        to: { name: "social-media-account-summary" },
      },
    },
    component: () => import("@/views/SocialMedia/SocialMediaReport.vue"),
    children: [
      {
        path: "account-summary",
        name: "social-media-account-summary",
        meta: { title: "Overview", breadcrumb: false },
        component: () =>
          import("@/views/SocialMedia/SocialMediaAccountTab.vue"),
      },
      {
        path: "posts",
        name: "Posts",
        redirect: { name: "social-media-posts" },
        meta: {
          title: "Posts",
          breadcrumb: { label: "Posts", to: { name: "social-media-posts" } },
        },
        children: [
          {
            path: "",
            name: "social-media-posts",
            meta: { title: "Posts", breadcrumb: false },
            component: () =>
              import("@/views/SocialMedia/SocialMediaPostsTab.vue"),
          },
          {
            path: ":id",
            name: "social-media-post",
            meta: {
              title: "Social Media Post",
              breadcrumb: {
                label: "Post",
                dynamic: true,
                fallback: "Social media post",
              },
            },
            component: () =>
              import("@/views/SocialMedia/SocialMediaSinglePostView.vue"),
          },
        ],
      },
      {
        path: "pages",
        name: "social-media-pages",
        meta: { title: "Pages", excludeFromNavbar: true },
        redirect: (to) => ({
          name: "social-media-account-summary",
          query: {
            ...to.query,
            share: migrateLegacyPagesShare(to.query.share),
          },
        }),
      },
    ],
  },
  {
    path: "search-term-report",
    alias: "/search-term-report",
    name: "search-term-report-base",
    redirect: { name: "search-term-report" },
    meta: {
      activityArea: "ad_performance",
      title: "Search Term Report",
      text: "Search Term Report",
      icon: "file-magnifying-glass-light",
      description: "Discover keywords people use to find your business online",
      updated: "2021-04-20",
      helpDeskPath: "search-term-report",
      breadcrumb: {
        label: "Search Terms",
        to: { name: "search-term-report" },
      },
    },
    children: [
      {
        path: "",
        name: "search-term-report",
        meta: { title: "Search Term Report", breadcrumb: false },
        component: () =>
          import("@/views/SearchTermReport/SearchTermReport.vue"),
      },
      {
        path: ":id",
        name: "search-term-detail",
        meta: {
          title: "Search Term Details",
          requiredResources: [
            ResourceChannel.GOOGLE_ADS,
            ResourceChannel.BING_ADS,
            ResourceChannel.GOOGLE_SEARCH_CONSOLE,
          ],
          breadcrumb: {
            label: "Search term",
            dynamic: true,
            fallback: "Search term",
          },
        },
        component: () =>
          import("@/views/SearchTermReport/SearchTermDetailReport.vue"),
      },
    ],
  },
];
